<template>
    <modal
        ref="modal"
        id="payment-billing-data-modal"
        :centered="true"
        :title="$t('order.paymentBillingDataRequiredTitle')"
        :show="modal.show"
        :close="modal.close"
        :closeable="true"
        @modal-opening="onModalOpening"
        @modal-visible="onResize"
        @modal-close="onModalClosing"
        @modal-invisible="
            modal.show = false;
            modal.close = false;
        "
        size="modal-lg"
    >
        <form method="POST" action="" enctype="multipart/form-data" @submit.prevent="onFormSubmit" :style="formStyle">
            <div class="modal-body" :style="bodyStyle">
                <p class="text-muted" v-html="$t('order.paymentBillingDataRequiredText', infoTextPlaceholders)"></p>

                <div class="row">
                    <div class="col-12 col-md-6">
                        <div class="form-group">
                            <label for="billingData-firstname" v-html="$t('shop.first_name')"></label>
                            <input
                                type="text"
                                name="billingData[firstname]"
                                class="form-control"
                                id="billingData-firstname"
                                :placeholder="$t('shop.first_name_placeholder')"
                                @input="onInputChange('first_name')"
                                v-model="invoice.first_name"
                                :class="{ 'is-invalid': formErrors.first_name }"
                                required
                                @focus="onInputFocus"
                            />
                            <div class="invalid-feedback">
                                <span v-for="error in formErrors.first_name" :key="error" class="d-block">{{
                                    error
                                }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="col-12 col-md-6">
                        <div class="form-group">
                            <label for="billingData-lastname" v-html="$t('shop.last_name')"></label>
                            <input
                                type="text"
                                name="billingData[lastname]"
                                class="form-control"
                                id="billingData-lastname"
                                :placeholder="$t('shop.last_name_placeholder')"
                                @input="onInputChange('last_name')"
                                v-model="invoice.last_name"
                                :class="{ 'is-invalid': formErrors.last_name }"
                                required
                                @focus="onInputFocus"
                            />
                            <div class="invalid-feedback">
                                <span v-for="error in formErrors.last_name" :key="error" class="d-block">{{
                                    error
                                }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="col-12">
                        <div class="form-group">
                            <label for="billingData-company" v-html="$t('shop.company')"></label>
                            <input
                                type="text"
                                name="billingData[company]"
                                class="form-control"
                                id="billingData-company"
                                :placeholder="$t('shop.company_placeholder')"
                                @input="onInputChange('company')"
                                v-model="invoice.company"
                                :class="{ 'is-invalid': formErrors.company }"
                                @focus="onInputFocus"
                            />
                            <div class="invalid-feedback">
                                <span v-for="error in formErrors.company" :key="error" class="d-block">{{
                                    error
                                }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="col-12 col-md-8">
                        <div class="form-group">
                            <label for="billingData-street" v-html="$t('shop.street')"></label>
                            <input
                                type="text"
                                name="billingData[street]"
                                class="form-control"
                                id="billingData-street"
                                :placeholder="$t('shop.street_placeholder')"
                                @input="onInputChange('street')"
                                v-model="invoice.street"
                                :class="{ 'is-invalid': formErrors.street }"
                                required
                                @focus="onInputFocus"
                            />
                            <div class="invalid-feedback">
                                <span v-for="error in formErrors.street" :key="error" class="d-block">{{ error }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="col-12 col-md-4">
                        <div class="form-group">
                            <label for="billingData-house" v-html="$t('shop.house_number')"></label>
                            <input
                                type="text"
                                name="billingData[house]"
                                class="form-control"
                                id="billingData-house"
                                :placeholder="$t('shop.house_number_placeholder')"
                                @input="onInputChange('house')"
                                v-model="invoice.houseNumber"
                                :class="{ 'is-invalid': formErrors.house }"
                                required
                                @focus="onInputFocus"
                            />
                            <div class="invalid-feedback">
                                <span v-for="error in formErrors.house" :key="error" class="d-block">{{ error }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="col-12 col-md-4">
                        <div class="form-group">
                            <label for="billingData-postcode" v-html="$t('shop.postcode')"></label>
                            <input
                                type="text"
                                name="billingData[postcode]"
                                class="form-control"
                                id="billingData-postcode"
                                :placeholder="$t('shop.postcode_placeholder')"
                                @input="onInputChange('postcode')"
                                v-model="invoice.postcode"
                                :class="{ 'is-invalid': formErrors.postcode }"
                                required
                                @focus="onInputFocus"
                            />
                            <div class="invalid-feedback">
                                <span v-for="error in formErrors.postcode" :key="error" class="d-block">{{
                                    error
                                }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="col-12 col-md-8">
                        <div class="form-group">
                            <label for="billingData-city" v-html="$t('shop.city')"></label>
                            <input
                                type="text"
                                name="billingData[city]"
                                class="form-control"
                                id="billingData-city"
                                :placeholder="$t('shop.city_placeholder')"
                                @input="onInputChange('city')"
                                v-model="invoice.city"
                                :class="{ 'is-invalid': formErrors.city }"
                                required
                                @focus="onInputFocus"
                            />
                            <div class="invalid-feedback">
                                <span v-for="error in formErrors.city" :key="error" class="d-block">{{ error }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="col-12">
                        <div class="form-group">
                            <label for="billingData-country" v-html="$t('shop.country')"></label>
                            <select
                                name="billingData[country]"
                                class="form-control"
                                id="billingData-country"
                                @change="onInputChange('country')"
                                v-model="invoice.country"
                                :class="{ 'is-invalid': formErrors.country }"
                                required
                                @focus="onInputFocus"
                            >
                                <option :value="null" disabled>{{ $t('shop.dropdown_required') }}</option>
                                <option
                                    v-for="country in countries"
                                    :value="country.country_name"
                                    :key="country.country_name"
                                >
                                    {{ $t('country.' + country.country_code) }}
                                </option>
                            </select>
                            <div class="invalid-feedback">
                                <span v-for="error in formErrors.country" :key="error" class="d-block">{{
                                    error
                                }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="modal-footer inline-footer">
                <button type="submit" class="btn btn-primary" v-html="$t('order.saveBillingDataAndContinue')"></button>

                <button
                    type="button"
                    class="btn btn-secondary"
                    @click.prevent="onAbortClicked"
                    v-html="$t('generic.cancel')"
                ></button>
            </div>
        </form>
    </modal>
</template>

<script>
export default {
    props: {
        order: {
            type: Object,
            required: true,
        },
        countries: {
            type: Array,
            required: true,
        },
        paymentMethod: {
            type: Object,
            required: true,
        },
        paymentSubtype: {
            type: Object,
            required: false,
            default() {
                return null;
            },
        },
        updateOrderInvoiceUrl: {
            type: String,
            required: true,
        },
    },

    data() {
        // Determine the default country selection
        const country = this.countries.filter((country) => country.country_code === this.order.country_code)[0] || null;

        return {
            modal: {
                show: false,
                close: false,
            },
            invoice: {
                first_name: this.order.first_name || this.order.paypal_first_name,
                last_name: this.order.last_name || this.order.paypal_last_name,
                company: this.order.company,
                street: this.order.street,
                houseNumber: this.order.house,
                postcode: this.order.postcode,
                city: this.order.city,
                country: this.order.country || (country ? country.country_name : null),
            },
            formErrors: {},
            changedInputs: {
                first_name: false,
                last_name: false,
                company: false,
                street: false,
                house: false,
                postcode: false,
                city: false,
                country: false,
            },
            isLoading: false,
            formStyle: {
                height: '100%',
            },
            bodyStyle: {
                height: '100%',
            },
        };
    },

    computed: {
        /**
         * Handle the load and definition of available placeholders to use for the info text at the modal.
         *
         * @returns {Object}
         */
        infoTextPlaceholders() {
            // In the case a payment subtype was given at the mounting place of this component.
            if (typeof this.paymentSubtype === 'object' && this.paymentSubtype !== null) {
                return {
                    name: this.$t(this.paymentSubtype.name),
                };
            }

            return {
                name: this.$t(this.paymentMethod.name),
            };
        },
    },

    methods: {
        /**
         * Handle the submit of form and trigger the continue of payment process, if all necessary data was entered.
         *
         * @returns {void}
         */
        onFormSubmit() {
            // In the case that a request is already running to update the data.
            if (this.isLoading) {
                return;
            }

            // Prevent another submit of the form, while this request is ongoing.
            this.isLoading = true;
            this.formErrors = {};

            // Build the request data to handle the update of those details.
            const request = new FormData();
            request.append('first_name', this.invoice.first_name);
            request.append('last_name', this.invoice.last_name);
            request.append('company', this.invoice.company === null ? '' : this.invoice.company);
            request.append('street', this.invoice.street);
            request.append('house', this.invoice.houseNumber);
            request.append('postcode', this.invoice.postcode);
            request.append('city', this.invoice.city);
            request.append('country', this.invoice.country);

            axios
                .post(this.updateOrderInvoiceUrl, request)
                .then((response) => {
                    // If the request is failed, we have to pass the form validation errors into the form errors
                    // property to show the user the exact reason of failure.
                    if (!response.data.success) {
                        switch (response.data.message) {
                            case 'validity':
                                // Before we apply the errors, we should filter the empty fields,
                                // from response. To prevent an error message on not entered inputs.
                                for (const key in response.data.details) {
                                    if (typeof this.changedInputs[key] === 'undefined' || !this.changedInputs[key]) {
                                        delete response.data.details[key];
                                    }
                                }

                                this.formErrors = response.data.details;

                                break;
                        }

                        this.isLoading = false;

                        return;
                    }

                    // If the mandatory data is entered, we can emit a event to parent to indicate this
                    // and trigger the continue of payment process.
                    if (typeof response.data.paymentContinuable === 'boolean' && response.data.paymentContinuable) {
                        this.$nextTick(() => {
                            this.$emit('confirmed');
                        });

                        return;
                    }

                    this.isLoading = false;
                })
                .catch(() => {
                    this.isLoading = false;
                });
        },

        /**
         * Handle the flagging of a changed input to enable form error message display.
         *
         * @param {string} fieldName
         *
         * @returns {void}
         */
        onInputChange(fieldName) {
            this.changedInputs[fieldName] = true;
        },

        /**
         * Handle the callback of a input focus.
         *
         * @returns {void}
         */
        onInputFocus() {
            this.$nextTick(() => {
                this.onResize();
            });
        },

        /**
         * Handle the event callback for clicked cancel button.
         *
         * @returns {void}
         */
        onAbortClicked() {
            this.modal.close = true;
            this.$nextTick(() => {
                this.$emit('aborted');
            });
        },

        /**
         * Handle the callback of resize event, to assure the header size will be tracked correctly.
         *
         * @returns {void}
         */
        onResize() {
            // In the case that setup popup isn't visible, we don't have to handle the update.
            if (!this.modal.show) {
                return;
            }

            // Determine the height of the modal title container, to assure
            // we apply the correct value for the form container.
            const titleHeight = this.$refs.modal.$el.querySelector('.modal-header').clientHeight;
            const footerHeight = this.$refs.modal.$el.querySelector('.modal-footer').clientHeight;

            this.formStyle.height = `calc(100% - ${titleHeight}px)`;
            this.bodyStyle.height = `calc(100% - ${footerHeight}px)`;
        },

        /**
         * Handle the event callback of opening modal.
         *
         * @returns {void}
         */
        onModalOpening() {
            this.$el.ownerDocument.body.classList.add('overflow-hidden');
        },

        /**
         * Handle the event callback for closing modal.
         *
         * @returns {void}
         */
        onModalClosing() {
            this.$el.ownerDocument.body.classList.remove('overflow-hidden');
            this.onAbortClicked();
        },
    },

    beforeUnmount() {
        window.removeEventListener('resize', () => {
            this.onResize();
        });
    },

    beforeMount() {
        window.addEventListener('resize', () => {
            this.onResize();
        });
    },

    mounted() {
        this.modal.show = true;
    },
};
</script>
